import React from "react";
import { Form, Spinner, Alert } from "react-bootstrap";
import Select from "react-select";
import { Controller } from "react-hook-form";
import { useEventTags } from "./hooks/useEventTags";

const TagsSelector = ({
  // Props obrigatórias
  control,
  name = "tags",

  // Props opcionais de configuração
  label = "Tags",
  placeholder = "Selecione as tags...",
  helperText = "Selecione tags existentes",
  maxTags = null,
  isRequired = false,

  // Props de filtragem
  category = null, // 'appointment', 'client', 'contact', 'chat', 'general' ou null para todas
  includeGeneralTags = true, // Se deve incluir tags gerais junto com a categoria específica

  // Props de estilo
  size = "default", // 'sm', 'default', 'lg'
  variant = "default", // 'default', 'minimal', 'bordered'
  showPreview = true,
  showUsageCount = true,

  // Props de comportamento
  autoFocus = false,
  disabled = false,
  clearable = true,

  // Props de callback
  onTagsChange = null, // Callback quando tags mudam

  // Props de validação
  validation = {},

  // Props para valores iniciais
  defaultValue = "",
}) => {
  const {
    tags,
    loading: tagsLoading,
    error: tagsError,
    parseTagsString,
    formatTagsForSave,
  } = useEventTags();

  // Filtrar tags baseado na categoria
  const getFilteredTagOptions = () => {
    let filteredTags = tags;

    if (category) {
      filteredTags = tags.filter((tag) => {
        if (tag.category === category) return true;
        if (includeGeneralTags && tag.category === "general") return true;
        return false;
      });
    }
    
    return filteredTags.map((tag) => ({
      value: tag.key_name,
      label: tag.key_name,
      color: tag.color,
      id: tag.id,
      isExisting: true,
      usage_count: tag.usage_count,
      category: tag.category,
    }));
  };

  // Styles customizados baseado no variant e size
  const getCustomStyles = () => {
    const baseHeight = size === "sm" ? "36px" : size === "lg" ? "48px" : "42px";
    const fontSize =
      size === "sm" ? "0.8rem" : size === "lg" ? "1rem" : "0.875rem";

    const borderStyle =
      variant === "minimal"
        ? "none"
        : variant === "bordered"
        ? "2px solid #ced4da"
        : "1px solid #ced4da";

    return {
      control: (provided, state) => ({
        ...provided,
        minHeight: baseHeight,
        border: borderStyle,

        borderColor: state.isFocused ? "#80bdff" : "#ced4da",
        boxShadow:
          state.isFocused && variant !== "minimal"
            ? "0 0 0 0.2rem rgba(0, 123, 255, 0.25)"
            : "none",
        "&:hover": {
          borderColor: "#80bdff",
        },
        fontSize: fontSize,
        borderRadius: variant === "minimal" ? "0" : "0.375rem",
        backgroundColor: disabled ? "#f8f9fa" : "white",
      }),
      multiValue: (provided, { data }) => ({
        ...provided,
        backgroundColor: data.color ? `${data.color}20` : "#e9ecef",
        borderRadius: "4px",
        border: `1px solid ${data.color || "#ced4da"}`,
        fontSize: fontSize,
      }),
      multiValueLabel: (provided, { data }) => ({
        ...provided,
        color: data.color || "#495057",
        fontWeight: "500",
        fontSize: fontSize,
        cursor: "pointer",
      }),
      multiValueRemove: (provided, { data }) => ({
        ...provided,
        color: data.color || "#495057",
        ":hover": {
          backgroundColor: data.color || "#dc3545",
          color: "white",
        },
      }),
      option: (provided, { data, isSelected, isFocused }) => ({
        ...provided,
        backgroundColor: isSelected
          ? data.color || "#007bff"
          : isFocused
          ? `${data.color || "#007bff"}20`
          : "white",
        color: isSelected ? "white" : data.color || "#495057",
        display: "flex",
        alignItems: "center",
        fontWeight: "500",
        fontSize: fontSize,
        cursor: "pointer",
        paddingLeft: "8px",
        textTransform: "capitalize",
      }),
      placeholder: (provided) => ({
        ...provided,
        color: "#6c757d",
        fontSize: fontSize,
      }),
      menu: (provided) => ({
        ...provided,
        zIndex: 1060,
      }),
    };
  };

  // Componente para opções
  const OptionComponent = (props) => {
    const { data, children, innerRef, innerProps } = props;

    return (
      <div ref={innerRef} {...innerProps} className="tag-option">
        <div className="tag-option-content">
          <div
            className="tag-option-color"
            style={{
              backgroundColor: data.color || "#45B7D1",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              marginRight: "8px",
              display: "inline-block",
              cursor: "pointer", 
              marginLeft: "5px",
              textTransform: 'capitalize'
            }}
          />
          <span className="tag-option-label">
            {children}
            {showUsageCount && data.usage_count > 0 && (
              <span
                className="tag-usage-count"
                style={{ marginLeft: "8px", color: "#6c757d" }}
              >
                ({data.usage_count})
              </span>
            )}
          </span>
        </div>
      </div>
    );
  };

  // Converter valor string para array de opções
  const getSelectedTags = (value) => {
    if (!value || !tags.length) return [];
    
    const parsedTags = parseTagsString(value);
    return parsedTags;
  };

  // Validação customizada
  const getValidationRules = () => {
    const rules = { ...validation };

    if (isRequired) {
      rules.required = rules.required || "Este campo é obrigatório";
    }

    if (maxTags) {
      rules.validate = {
        ...rules.validate,
        maxTags: (value) => {
          if (!value) return true;
          const tagCount = value.split(",").filter((tag) => tag.trim()).length;
          return tagCount <= maxTags || `Máximo de ${maxTags} tags permitidas`;
        },
      };
    }

    return rules;
  };

  return (
    <div
      className={`tags-selector tags-selector--${variant} tags-selector--${size}`}
    >
      <Form.Group>
        {label && (
          <Form.Label className="d-flex align-items-center gap-2">
            {label}
            {isRequired && <span className="text-danger">*</span>}
            {tagsLoading && (
              <Spinner animation="border" size="sm" className="ms-2" />
            )}
          </Form.Label>
        )}

        {tagsError && (
          <Alert variant="warning" className="py-2 mb-2">
            <small>{tagsError}</small>
          </Alert>
        )}

        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue}
          rules={getValidationRules()}
          render={({ field, fieldState }) => {
            const selectedTags = getSelectedTags(field.value);

            return (
              <>
                <Select
                  isMulti
                  value={selectedTags}
                  onChange={(selectedOptions) => {
                    // Verificar limite de tags
                    if (
                      maxTags &&
                      selectedOptions &&
                      selectedOptions.length > maxTags
                    ) {
                      return; // Impede a seleção se exceder o limite
                    }

                    const formattedValue = formatTagsForSave(
                      selectedOptions || []
                    );
                    field.onChange(formattedValue);

                    if (onTagsChange) {
                      onTagsChange(selectedOptions || []);
                    }
                  }}
                  options={getFilteredTagOptions()}
                  styles={getCustomStyles()}
                  components={{
                    Option: OptionComponent,
                  }}
                  placeholder={placeholder}
                  noOptionsMessage={() => "Nenhuma tag encontrada"}
                  isDisabled={disabled || tagsLoading}
                  isLoading={tagsLoading}
                  isClearable={clearable}
                  closeMenuOnSelect={false}
                  blurInputOnSelect={false}
                  classNamePrefix="tags-select"
                  className="tags-select-container"
                  menuPlacement="auto"
                  maxMenuHeight={200}
                  autoFocus={autoFocus}
                />

                {fieldState.error && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {fieldState.error.message}
                  </Form.Control.Feedback>
                )}
              </>
            );
          }}
        />

        {helperText && !tagsError && (
          <Form.Text className="text-muted">
            {helperText}
            {maxTags && <span className="text-muted"> (máx: {maxTags})</span>}
          </Form.Text>
        )}
      </Form.Group>

      {/* Preview das tags selecionadas */}
      {showPreview && (
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const selectedTags = getSelectedTags(field.value);

            if (selectedTags.length === 0) return null;
          }}
        />
      )}
    </div>
  );
};

export default TagsSelector;
